import Core from "@/core/Core";
import Dialog from './Dialog';
import DialogsRegistry from './DialogsRegistry';
import ActiveDialog from "@/core/services/Dialogs/ActiveDialog";
import DialogsRouter from "@/core/services/Dialogs/DialogsRouter";
import SentryService from "@/core/services/Sentry/SentryService";

/**
 * Сервис управления диалогами
 * Внутренний класс, доступ через DialogsRegistry
 */
class DialogsService {
  /**
   * Карта активных диалогов с сохранением порядка открытия
   * Используем ref для реактивности Map в Vue 3
   */
  static activeDialogs = ref(new Map<string, ActiveDialog>());

  /**
   * Счетчик z-index для корректного наслоения диалогов.
   * Стартует со слоя диалогов (130) и занимает полосу 130–139,
   * следующая ступень лестницы — 140 (PersonCard).
   */
  private static zIndexCounter: number = 130;

  /**
   * Флаг инициализации сервиса
   */
  private static isInitialized: boolean = false;

  /**
   * Инициализация сервиса
   */
  static init(): typeof DialogsService {
    if (this.isInitialized) {
      Core.warn('DialogsService уже инициализирован');
      return this;
    }

    document.addEventListener('keydown', this.handleEscapeKey.bind(this));

    this.isInitialized = true;
    //Core.debug('🖼️ DialogsService инициализирован');
    return this;
  }

  /**
   * Показать диалог
   * @param dialog - Диалог из DialogRegistry или строковое имя для совместимости
   * @param componentProps - Данные передаваемые в диалог как props
   * @param eventHandlers - Обработчики событий диалога (save, close, approve, decline, etc.)
   * @param contentSlots - Контент для слотов диалога (например, {default: 'Текст сообщения'})
   * @returns ID созданного диалога
   */
  static show(
    dialog: Dialog | string,
    componentProps: Record<string, any> = {},
    eventHandlers: Record<string, (data?: any) => void> = {},
    contentSlots: Record<string, any> | string | null = null
  ): string {

    const normalizedDialog = this.getDialog(dialog);
    const dialogId = `${normalizedDialog.name}_${Date.now()}`;

    // Дефолтный close-обработчик закрывает диалог в ответ на эмит close из компонента.
    // hideIfOpen с точным dialogId: VDialogV3 после эмита сам зовёт hideIfOpen —
    // кто бы ни закрыл первым, второй вызов молча пропускается, без warning.
    const normalizedHandlers = {
      close: () => this.hideIfOpen(dialogId),
      ...eventHandlers // пользовательские обработчики перезаписывают стандартные
    };

    // Собираем объект и кладем в коллекцию, DialogsContainer далее передаст все свойства в компонент
    const activeDialog = new ActiveDialog(
      dialogId,
      normalizedDialog.component,
      componentProps,
      contentSlots,
      normalizedHandlers,
      this.zIndexCounter++,
      normalizedDialog.name,
      normalizedDialog.routeEntityName
    );

    // Используем .value для работы с Map внутри ref
    this.activeDialogs.value.set(dialogId, activeDialog);


    // Sentry breadcrumb
    SentryService.trackDialog(normalizedDialog.name, 'open');

    Core.eventBus.UiModalOpened.emit({dialogName: normalizedDialog.name, dialogId});

    return dialogId;
  }


  /**
   * Проверить, открыт ли диалог с указанным идентификатором (ID или имя).
   */
  static isOpen(identifier: Dialog | string): boolean {
    return this.findDialogId(identifier) !== null
  }

  /**
   * Закрыть диалог, если он открыт. В отличие от hide(), не пишет warning,
   * если диалога с таким идентификатором нет — просто молча ничего не делает.
   *
   * Удобно для случаев, когда вызывающий код не знает наверняка, был ли диалог
   * показан (например, при cleanup'е компонента, который мог не открыть диалог).
   */
  static hideIfOpen(identifier: Dialog | string): void {
    if (this.isOpen(identifier) === false) return;

    this.hide(identifier);
  }

  /**
   * Зарегистрировать канал пользовательского закрытия для активного диалога.
   * VDialogV3 зовёт при маунте, передавая свой close() — через него идёт
   * закрытие по Escape: с эмитом close и confirmBeforeClose.
   *
   * Молча игнорирует незарегистрированный идентификатор: VDialogV3 мог быть
   * замонтирован вне системы диалогов.
   */
  static setCloseRequest(identifier: Dialog | string, closeRequest: () => void): void {
    const dialogId = this.findDialogId(identifier);
    if (dialogId === null) return;

    this.activeDialogs.value.get(dialogId)!.closeRequest = closeRequest;
  }

  /**
   * Скрыть диалог по объекту Dialog, имени или ID
   * @param identifier - Диалог, строковое имя или ID диалога
   */
  static hide(identifier: Dialog | string): void {
    const dialogId = this.findDialogId(identifier);
    if (dialogId === null) {
      Core.warn(`Попытка закрыть несуществующий диалог: ${identifier}`);
      return;
    }

    const dialog = this.activeDialogs.value.get(dialogId)!;

    this.activeDialogs.value.delete(dialogId);

    // handlers.close здесь не вызываем: close — это «пользователь закрыл диалог»,
    // его эмитит VDialogV3.close(). Программный hide() (например, после submit'а)
    // отменой не является и close-обработчики триггерить не должен.

    // А вот запись из URL убираем в любом случае: ?dialog=... отражает факт того,
    // что диалог открыт, а не способ его закрытия. Раньше очистка висела на
    // close-обработчике внутри Dialog.show(), и потому не срабатывала на
    // программных путях — hide() после submit'а, resolve(), hideAll(). Урл
    // оставался с записью о карточке, которой уже нет. Сюда сходятся все пути
    // закрытия, поэтому чистим здесь.
    this.removeFromUrl(dialog);

    // Sentry breadcrumb
    SentryService.trackDialog(dialog.dialogName, 'close');

    Core.eventBus.UiModalClosed.emit({
      dialogName: dialog.dialogName,
      dialogId,
    });
  }

  /**
   * Убрать запись о диалоге из URL. Парная к DialogsRouter.push в Dialog.show():
   * push при показе, remove при закрытии, независимо от того, кто закрыл.
   *
   * Ничего не делает для диалога без роутинга и для открытого без числового id —
   * в обоих случаях show() записи в URL и не создавал.
   */
  private static removeFromUrl(dialog: ActiveDialog): void {
    const entityId = dialog.props['id'];

    if (dialog.routeEntityName === null) {
      // диалог без роутинга — в URL его не было
    } else if (typeof entityId !== 'number') {
      // открыт без id (например, режим создания) — в URL его не было
    } else {
      DialogsRouter.remove(dialog.routeEntityName, entityId);
    }
  }

  /**
   * Скрыть все активные диалоги
   */
  static hideAll(): void {
    if (this.activeDialogs.value.size === 0) {
      Core.debug('Нет активных диалогов для закрытия');
      return;
    }

    const dialogsToClose = [...this.activeDialogs.value.entries()];

    for (const [dialogId] of dialogsToClose) {
      this.hide(dialogId);
    }
  }

  /**
   * Получить диалог по имени или вернуть готовый диалог
   */
  private static getDialog(dialog: Dialog | string): Dialog {
    if (typeof dialog === 'string') {
      const found = DialogsRegistry.getDialogByComponentName(dialog);
      if (!found) {
        throw new Error(`Dialog component "${dialog}" not found in registry`);
      }
      return found;
    }

    if (!(dialog instanceof Dialog)) {
      throw new Error('Некорректный объект диалога. Используйте диалоги из DialogRegistry.');
    }

    return dialog;
  }

  /**
   * Обработчик клавиши Escape
   * @param event
   */
  static handleEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      const topDialog = this.getTopDialog();
      if (topDialog) {
        if (topDialog.closeRequest !== null) {
          // Путь пользовательского закрытия: VDialogV3.close() —
          // эмит close + confirmBeforeClose, как у крестика и оверлея
          topDialog.closeRequest();
        } else {
          // Легаси-диалог без VDialogV3 — закрываем напрямую, по точному id
          this.hide(topDialog.id);
        }
      }
    }
  }

  /**
   * Получить информацию о верхнем диалоге (последний в стеке)
   * @returns ActiveDialog или null
   */
  static getTopDialog(): ActiveDialog | null {
    // Используем .value для работы с Map внутри ref
    if (this.activeDialogs.value.size === 0) return null;

    // Map сохраняет порядок вставки, берем последний
    const dialogs = [...this.activeDialogs.value.values()];
    return dialogs[dialogs.length - 1];
  }

  /**
   * Найти ID активного диалога по идентификатору. Идентификатор может быть:
   * - сам ID активного диалога (быстрый путь);
   * - объект Dialog или строковое имя — ищется последний открытый с этим именем.
   *
   * Возвращает null, если ничего не найдено.
   */
  private static findDialogId(identifier: Dialog | string): string | null {
    // Сначала пробуем найти по ID (быстрый путь)
    if (this.activeDialogs.value.has(identifier as string)) {
      return identifier as string;
    }

    // Если не ID, ищем по имени диалога — с конца, чтобы взять последний открытый
    const { name } = this.getDialog(identifier);
    const entries = Array.from(this.activeDialogs.value.entries());

    // Ищем с конца — при одинаковых dialogName возьмётся последний открытый
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, dialog] = entries[i];

      if (dialog.dialogName === name) {
        return id;
      }
    }

    return null;
  }
}

export default DialogsService;